import type { Product } from '@kimuno/api-sdk/lib/types/responses/Product';
import { createSelector } from 'reselect';

import { AsyncActionStatus } from '../actions/common.actionTypes';
import type { StoreType } from './index';
import { getProducts } from './products.selectors';

export const getSubjectProducts = (state: StoreType, subjectId: string, prefix?: string) =>
	state.dashboard.productsBySubjectId[prefix ? `${prefix}-${subjectId}` : subjectId];

export const getProductsBySubjectId = createSelector(
	[getSubjectProducts, getProducts],
	(products, allProducts) => {
		if (!products) return [];

		return products.ids.map((productId) => allProducts[productId]?.data as Product).filter((p) => p);
	}
);

// Per-instance variant of `getProductsBySubjectId`. The shared selector above
// memoizes a single (subjectId, prefix) pair, so several rows reading different
// pairs thrash its cache and each gets a fresh array on every unrelated store
// update. A component that renders many rows should build one of these per row
// (via useMemo) so each pair keeps a stable reference across those updates.
export const makeGetProductsBySubjectId = () =>
	createSelector([getSubjectProducts, getProducts], (products, allProducts) => {
		if (!products) return [];

		return products.ids.map((productId) => allProducts[productId]?.data as Product).filter((p) => p);
	});

export const getProductsBySubjectIdStatus = createSelector([getSubjectProducts], (products) => {
	if (!products) return AsyncActionStatus.INITIAL;

	return products.status;
});

export const getNewCourses = (state: StoreType) => state.dashboard.newCourses?.data;

export const getNewCoursesStatus = (state: StoreType) => state.dashboard.newCourses?.status;

export const getDashboardWorksheets = (state: StoreType) =>
	state.dashboard.dashboardWorksheets?.data ?? [];

export const getDashboardWorksheetsStatus = (state: StoreType) =>
	state.dashboard.dashboardWorksheets?.status ?? AsyncActionStatus.INITIAL;

export const getDashboardWorksheetsSubjectsKey = (state: StoreType) =>
	state.dashboard.dashboardWorksheets?.subjectsKey ?? '';
